/**
 * The `(projectId, taskId)` a `task_create` command addresses (IR-126 §4.3).
 *
 * Four call sites had their own copy of the two regexes: the three accept paths
 * (`updateTaskReview`, `acceptAllInSubject`, and the TD section store's
 * `acceptTask`) plus `rejectAllInSubject`, which is not part of the accept
 * migration but sat fifty lines from one that is. All four agreed, which is the
 * only reason nothing had gone wrong — an edit to one of four is how that stops
 * being true.
 *
 * **The id is the proposal's own document id, deliberately.** `apply_create`
 * gives the task it creates the proposal's id (§7.3), so this is both the
 * document being approved and the task that will exist. That correspondence is
 * what lets a caller mark pending state and record a recovery pointer *before*
 * the task exists.
 *
 * Returns `null` for anything that is not a proposal — a Kanban task path, a
 * collection, a document from another collection. Callers use that to mean
 * "this is not an approve", not "something went wrong": `updateTaskReview` is
 * also the un-accept entry point, and it reaches here with a Kanban path.
 */
const QMS_PROPOSAL = /^projects\/([^/]+)\/assessments\/[^/]+\/results\/[^/]+\/tasks\/([^/]+)$/
const TD_PROPOSAL = /^projects\/([^/]+)\/assessments\/[^/]+\/tasks\/([^/]+)$/

export interface ProposalTarget {
  projectId: string
  taskId: string
  /**
   * Which shape matched.
   *
   * QMS proposals live under `results/{rid}`, TD proposals directly under the
   * assessment. Kept because the two are ordered — TD is only tried when QMS
   * fails, since a QMS path would otherwise match the looser TD pattern's
   * prefix — and a caller that needs to know which it got should not re-derive
   * it by testing the path again.
   */
  kind: 'qms' | 'td'
}

export function proposalTarget(taskPath: string | null | undefined): ProposalTarget | null {
  if (!taskPath) return null

  const qms = QMS_PROPOSAL.exec(taskPath)
  if (qms) return { projectId: qms[1], taskId: qms[2], kind: 'qms' }

  const td = TD_PROPOSAL.exec(taskPath)
  if (td) return { projectId: td[1], taskId: td[2], kind: 'td' }

  return null
}

/**
 * The target of a proposal on a Product's `td_latest`, the only TD assessment
 * whose proposals `review_td_task` decides (MYAC-1660); `null` for anything else,
 * including a TD proposal under another assessment id.
 */
export function tdLatestProposalTarget(taskPath: string | null | undefined): ProposalTarget | null {
  const target = proposalTarget(taskPath)
  if (target?.kind !== 'td') return null
  return taskPath?.split('/')[3] === 'td_latest' ? target : null
}
